import { AppDispatch } from "@/store";
import { setAuthenticated, setToken, setProfile, setSessions, setTotpEnabled, setFtpData } from "@/store/slices/authSlice";
import { storeAuthData } from "@/store/app/actions";
import { toast } from "sonner";
import { appendTokenToCmsRedirect, getTokenFromResponse } from "./redirectHelpers";
import { profileApi } from "@/api/profile";
import { checkRedirectPermissions } from "@/utils/redirectPermissionCheck";
import { recordRedirectAttempt, LOOP_REDIRECT_QUERY_PARAM } from "@/utils/redirectLoopGuard";
import type { LoginApiResponse, LoginResponseData } from "@/types/auth";
import type { NavigateFunction } from "react-router-dom";

interface HandlePostLoginParams {
  token: string;
  response?: LoginApiResponse;
  data?: LoginResponseData;
  redirect?: string;
  redirectOrigin?: string;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  userName?: string;
  successMessage?: string;
  showToast?: boolean;
}

export const handlePostLogin = async ({
  token,
  response,
  data,
  redirect,
  redirectOrigin,
  dispatch,
  navigate,
  userName,
  successMessage,
  showToast = true,
}: HandlePostLoginParams): Promise<void> => {
  // If backend provides cookie_chain_url, use it (server-side redirect chain)
  // This handles cross-domain cookie setting via HTTP 302 redirects
  if (data?.cookie_chain_url) {
    console.log('Using backend redirect chain:', data.cookie_chain_url);
    window.location.href = data.cookie_chain_url;
    return; // Let backend handle the redirect chain
  }

  dispatch(setAuthenticated(true));
  dispatch(setToken(token));
  
  if (response && data) {
    storeAuthData(dispatch, { ...response, data });
  } else if (response) {
    storeAuthData(dispatch, response);
  }

  if (showToast) {
    const message = successMessage || (userName ? `Welcome ${userName}!` : "Login successful!");
    toast.success(message);
  }

  // Fetch user data (profile, permissions, sessions) before navigation
  // This ensures permissions are available immediately on /home page
  const finalRedirect = redirect || redirectOrigin || data?.redirect || "/home";
  
  let userPermissions: string[] = [];
  
  // Fetch user data to get permissions
  try {
    const { profile, sessions, totpEnabled, ftpData } = await profileApi.getUserData({
      userName,
      userEmail: data?.email || response?.data?.email,
    });
    
    // Dispatch user data to Redux store
    if (profile && (profile.id || profile.email)) {
      dispatch(setProfile(profile));
      dispatch(setSessions(sessions));
      dispatch(setTotpEnabled(totpEnabled));
      if (ftpData) {
        dispatch(setFtpData(ftpData));
      }
      
      // Store permissions for redirect check
      userPermissions = profile.permissions || [];
    }
  } catch (error) {
    console.error('Failed to fetch user data post-login:', error);
  }

  const authToken = response ? getTokenFromResponse(response) : token;
  
  // Check if user has permission to access the redirect target (prevent infinite loop)
  if (finalRedirect.startsWith('http')) {
    const { hasAccess } = checkRedirectPermissions(finalRedirect, userPermissions);

    if (!hasAccess) {
      console.log('User lacks access to redirect target, navigating to /home with redirect param');
      const homeUrlWithRedirect = `/home?redirect=${encodeURIComponent(finalRedirect)}`;
      navigate(homeUrlWithRedirect);
      return;
    }

    const { isLooping, origin } = recordRedirectAttempt(finalRedirect);
    if (isLooping) {
      navigate(`/home?${LOOP_REDIRECT_QUERY_PARAM}=${encodeURIComponent(origin || finalRedirect)}`);
      return;
    }

    const redirectWithToken = appendTokenToCmsRedirect(finalRedirect, authToken);
    window.location.href = redirectWithToken;
  } else {
    // Internal navigation
    navigate(finalRedirect);
  }
};

